import type { FC, ReactNode } from 'react'

import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { Map as MapIcon, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { summarizeRack, thermalColor } from '@/lib/inventory'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { COLD_AISLE, RACK_D, RACK_W } from '@/components/datacenter/layout'
import { glass } from '@/components/datacenter/hud/primitives'

import { CATEGORY_META, STATUS_META } from '@/constants/inventory'

const Minimap: FC = (): ReactNode => {
    const { layout } = useInventory()
    const pose = useDatacenterStore(state => state.pose)
    const focus = useDatacenterStore(state => state.focus)
    const lens = useDatacenterStore(state => state.lens)
    const selectedRackId = useDatacenterStore(state => state.selectedRackId)
    const hoveredRackId = useDatacenterStore(state => state.hoveredRackId)
    const selectRack = useDatacenterStore(state => state.selectRack)
    const setHoveredRack = useDatacenterStore(state => state.setHoveredRack)
    const [collapsed, setCollapsed] = useState(false)
    const { bounds } = layout
    const width = bounds.maxX - bounds.minX
    const depth = bounds.maxZ - bounds.minZ

    const summaries = useMemo(() => new Map(layout.racks.map(rack => [rack.rack.id, summarizeRack(rack.rack, rack.devices)])), [layout])

    const fillFor = (rackId: string, category: keyof typeof CATEGORY_META) => {
        const summary = summaries.get(rackId)

        if (lens === 'health' && summary) return STATUS_META[summary.worst].color
        if (lens === 'thermal' && summary) return thermalColor(summary.avgTemp)

        return CATEGORY_META[category].color
    }

    const cone = 2.4
    const spread = 0.55
    const tipX = pose.x
    const tipZ = pose.z
    const leftX = tipX + Math.sin(pose.yaw - spread) * cone
    const leftZ = tipZ + Math.cos(pose.yaw - spread) * cone
    const rightX = tipX + Math.sin(pose.yaw + spread) * cone
    const rightZ = tipZ + Math.cos(pose.yaw + spread) * cone

    return (
        <motion.div
            className={cn(glass, 'pointer-events-auto absolute bottom-4 left-4 z-20 hidden w-60 overflow-hidden xl:block [@media(max-height:760px)]:hidden')}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className='flex items-center justify-between px-3 py-2'>
                <p className='flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500'>
                    <MapIcon className='size-3.5' aria-hidden='true' />
                    Floor plan
                </p>
                <button
                    type='button'
                    onClick={() => setCollapsed(value => !value)}
                    className='flex size-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-white/10 hover:text-white'
                    aria-label={collapsed ? 'Expand the floor plan' : 'Collapse the floor plan'}
                    aria-expanded={!collapsed}
                >
                    {collapsed ? <Plus className='size-3.5' aria-hidden='true' /> : <Minus className='size-3.5' aria-hidden='true' />}
                </button>
            </div>

            {!collapsed && (
                <svg viewBox={`${bounds.minX} ${bounds.minZ} ${width} ${depth}`} className='block w-full border-t border-white/10 bg-[#03060c]/60' role='img' aria-label='Floor plan of the data hall with your current position'>
                    <defs>
                        <pattern id='minimap-grid' width='0.6' height='0.6' patternUnits='userSpaceOnUse'>
                            <path d='M 0.6 0 L 0 0 0 0.6' fill='none' stroke='rgba(148,163,184,0.08)' strokeWidth='0.03' />
                        </pattern>
                        <radialGradient id='minimap-cone' cx='0' cy='0' r='1'>
                            <stop offset='0%' stopColor='#67e8f9' stopOpacity='0.55' />
                            <stop offset='100%' stopColor='#67e8f9' stopOpacity='0' />
                        </radialGradient>
                    </defs>

                    <rect x={bounds.minX} y={bounds.minZ} width={width} height={depth} fill='url(#minimap-grid)' stroke='rgba(148,163,184,0.35)' strokeWidth='0.08' />
                    <rect x={layout.door.x - 0.95} y={bounds.maxZ - 0.08} width={1.9} height={0.16} fill='#22d3ee' />

                    {layout.pods.map(pod => (
                        <rect key={pod.index} x={pod.x - COLD_AISLE / 2} y={pod.zMin} width={COLD_AISLE} height={pod.zMax - pod.zMin} fill='rgba(56,189,248,0.08)' />
                    ))}

                    {layout.racks.map(rack => {
                        const selected = rack.rack.id === selectedRackId
                        const hovered = rack.rack.id === hoveredRackId
                        const dimmed = focus !== 'overview' && rack.category !== focus

                        return (
                            <rect
                                key={rack.rack.id}
                                x={rack.x - RACK_D / 2 + 0.04}
                                y={rack.z - RACK_W / 2 + 0.03}
                                width={RACK_D - 0.08}
                                height={RACK_W - 0.06}
                                rx={0.05}
                                fill={fillFor(rack.rack.id, rack.category)}
                                fillOpacity={dimmed ? 0.18 : selected ? 1 : 0.7}
                                stroke={selected ? '#ffffff' : hovered ? '#67e8f9' : 'none'}
                                strokeWidth={selected ? 0.1 : 0.07}
                                className='cursor-pointer outline-none transition-[fill-opacity] duration-300 focus-visible:stroke-cyan-300'
                                role='button'
                                tabIndex={0}
                                aria-label={`Rack ${rack.rack.code}`}
                                onMouseEnter={() => setHoveredRack(rack.rack.id)}
                                onMouseLeave={() => setHoveredRack(null)}
                                onClick={() => selectRack(rack.rack.id)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        selectRack(rack.rack.id)
                                    }
                                }}
                            />
                        )
                    })}

                    <path d={`M ${tipX} ${tipZ} L ${leftX} ${leftZ} L ${rightX} ${rightZ} Z`} fill='url(#minimap-cone)' />
                    <circle cx={tipX} cy={tipZ} r={0.22} fill='#67e8f9' stroke='#03060c' strokeWidth={0.06}>
                        <animate attributeName='r' values='0.2;0.3;0.2' dur='2s' repeatCount='indefinite' />
                    </circle>
                </svg>
            )}
        </motion.div>
    )
}

export default Minimap
